/**
 * Supabase Client Configuration
 *
 * This module provides a singleton instance of the Supabase client for the application.
 * It automatically handles environment variable configuration with fallback values for local development.
 *
 * Usage:
 * ```typescript
 * import { supabase } from '@/lib/supabase';
 *
 * // Query from web schema views (recommended pattern)
 * const { data, error } = await supabase
 *   .schema('web')
 *   .from('v_kpi_active_startups_v1')
 *   .select('*');
 * ```
 *
 * Environment Variables:
 * - VITE_SUPABASE_URL: Supabase project URL (defaults to localhost for local development)
 * - VITE_SUPABASE_ANON_KEY: Supabase anonymous key (defaults to demo key for local development)
 *
 * Local Development:
 * - NO .env.local file is required for local development
 * - Fallback values automatically connect to local Supabase instance (http://localhost:54321)
 * - Local Supabase instance must be running via Docker
 *
 * Production:
 * - Environment variables are managed by Vercel deployment
 * - Never commit production credentials to version control
 */

import { createClient } from '@supabase/supabase-js';

/**
 * Environment-based Supabase Configuration
 *
 * Uses VERCEL_ENV to automatically route to correct Supabase instance:
 * - Production (main branch): PROD Supabase (uxyewjsxtgkpxoabuczr)
 * - Preview/Dev branches: DEV Supabase (bdxmxjnaeutcfqhrcwna)
 * - Local Development: Local Supabase instance (http://localhost:54321)
 */

// Supabase PROD configuration (for main branch production deployments)
const SUPABASE_PROD_URL = 'https://uxyewjsxtgkpxoabuczr.supabase.co';
const SUPABASE_PROD_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InV4eWV3anN4dGdrcHhvYWJ1Y3pyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTk0MDkyODUsImV4cCI6MjA3NDk4NTI4NX0.QyiVhoj7zRmhWLbqYvhn7dsDEIE9Jw_yg_AHLYIbgCU';

// Supabase DEV configuration (for dev/preview deployments and feature branches)
const SUPABASE_DEV_URL = 'https://bdxmxjnaeutcfqhrcwna.supabase.co';
const SUPABASE_DEV_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJkeG14am5hZXV0Y2ZxaHJjd25hIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDk1NjEzODQsImV4cCI6MjA2NTEzNzM4NH0.F5NWsub9E6F4uYnPL4i7725pC_DVRZkQyNWwh_p_rnE';

// Local development fallback
const SUPABASE_LOCAL_URL = 'http://localhost:54321';
const SUPABASE_LOCAL_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0';

/**
 * Determine which Supabase instance to use based on environment
 *
 * Logic:
 * 1. Check if VERCEL_ENV is available (Vercel deployment)
 *    - If "production": Use PROD Supabase
 *    - If "preview" or "development": Use DEV Supabase
 * 2. Check if VITE_SUPABASE_URL is set (manual override)
 * 3. Fall back to local development instance
 */
const getSupabaseCredentials = (): { url: string; anonKey: string } => {
  // Check VERCEL_ENV system variable (available in Vercel deployments)
  const vercelEnv = import.meta.env.VERCEL_ENV;

  if (vercelEnv === 'production') {
    // Production deployment (main branch)
    return {
      url: SUPABASE_PROD_URL,
      anonKey: SUPABASE_PROD_ANON_KEY,
    };
  } else if (vercelEnv === 'preview' || vercelEnv === 'development') {
    // Preview/development deployments (dev, feature branches)
    return {
      url: SUPABASE_DEV_URL,
      anonKey: SUPABASE_DEV_ANON_KEY,
    };
  }

  // Manual override via environment variables (legacy support)
  if (import.meta.env.VITE_SUPABASE_URL) {
    return {
      url: import.meta.env.VITE_SUPABASE_URL,
      anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || SUPABASE_LOCAL_ANON_KEY,
    };
  }

  // Local development fallback
  return {
    url: SUPABASE_LOCAL_URL,
    anonKey: SUPABASE_LOCAL_ANON_KEY,
  };
};

const { url: supabaseUrl, anonKey: supabaseAnonKey } = getSupabaseCredentials();

/**
 * Singleton Supabase Client Instance
 *
 * This client is shared across the entire application to avoid creating multiple connections.
 * It provides access to all Supabase features including:
 * - Database queries (Postgres REST API)
 * - Real-time subscriptions
 * - Authentication
 * - Storage
 *
 * @example
 * ```typescript
 * // Query data from the web schema (recommended pattern)
 * const { data, error } = await supabase
 *   .schema('web')
 *   .from('v_kpi_active_startups_v1')
 *   .select('*');
 *
 * // Subscribe to real-time changes
 * const subscription = supabase
 *   .channel('kpi-changes')
 *   .on('postgres_changes', {
 *     event: '*',
 *     schema: 'web',
 *     table: 'v_kpi_active_startups_v1'
 *   }, (payload) => {
 *     console.log('Change received!', payload);
 *   })
 *   .subscribe();
 * ```
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  db: {
    schema: 'web', // Default schema for all queries (web schema views with RLS)
  },
});

/**
 * Helper function to check if Supabase client is configured for production
 *
 * @returns {boolean} True if using production Supabase instance
 */
export const isProductionSupabase = (): boolean => {
  return import.meta.env.VERCEL_ENV === 'production';
};

/**
 * Helper function to get the current Supabase environment
 *
 * @returns {string} Current environment: 'production', 'preview', 'development', or 'local'
 */
export const getSupabaseEnvironment = (): string => {
  const vercelEnv = import.meta.env.VERCEL_ENV;
  if (vercelEnv === 'production') return 'production';
  if (vercelEnv === 'preview' || vercelEnv === 'development') return 'preview';
  if (import.meta.env.VITE_SUPABASE_URL) return 'custom';
  return 'local';
};

/**
 * Helper function to get the current Supabase configuration
 *
 * @returns {object} Current Supabase configuration (URL only, never expose keys)
 */
export const getSupabaseConfig = () => {
  return {
    url: supabaseUrl,
    environment: getSupabaseEnvironment(),
    isProduction: isProductionSupabase(),
  };
};
